import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Search, CheckCircle2, Download } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { CURRENCY_SYMBOL, formatCurrency } from "@/lib/currency";

const statusLabels: Record<string, string> = {
  pending: "معلق",
  paid: "مدفوع",
  overdue: "متأخر",
};

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-destructive/10 text-destructive",
};

const AccountantInvoices = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterNursery, setFilterNursery] = useState("all");

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["accountant-all-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, children(full_name), nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const markPaid = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invoices").update({
        status: "paid",
        paid_at: new Date().toISOString(),
        payment_method: "manual",
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تسجيل الدفع");
      queryClient.invalidateQueries({ queryKey: ["accountant-all-invoices"] });
    },
    onError: () => toast.error("فشل تسجيل الدفع"),
  });

  const nurseryNames = [...new Set(invoices?.map((i: any) => (i as any).nurseries?.name).filter(Boolean) || [])];

  const filtered = invoices?.filter((inv: any) => {
    if (search && !inv.title?.includes(search) && !(inv as any).children?.full_name?.includes(search)) return false;
    if (filterStatus !== "all" && inv.status !== filterStatus) return false;
    if (filterNursery !== "all" && (inv as any).nurseries?.name !== filterNursery) return false;
    return true;
  }) || [];

  const totalAmount = filtered.reduce((s, i) => s + (i.amount || 0), 0);
  const paidAmount = filtered.filter(i => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-primary" />
          إدارة الفواتير
        </h1>
        <p className="text-muted-foreground text-sm mt-1">عرض وإدارة فواتير جميع الحضانات</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">إجمالي المبلغ</p>
            <p className="text-2xl font-bold">{totalAmount.toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">المحصّل</p>
            <p className="text-2xl font-bold text-green-600">{paidAmount.toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">المتبقي</p>
            <p className="text-2xl font-bold text-amber-600">{(totalAmount - paidAmount).toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث بالعنوان أو اسم الطفل..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="الحالة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="pending">معلق</SelectItem>
                <SelectItem value="paid">مدفوع</SelectItem>
                <SelectItem value="overdue">متأخر</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterNursery} onValueChange={setFilterNursery}>
              <SelectTrigger className="w-44"><SelectValue placeholder="الحضانة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحضانات</SelectItem>
                {nurseryNames.map((n) => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">الفواتير ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>العنوان</TableHead>
                    <TableHead>الطفل</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الاستحقاق</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>إجراء</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((inv: any) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.title}</TableCell>
                      <TableCell>{(inv as any).children?.full_name || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{(inv as any).nurseries?.name || "—"}</Badge>
                      </TableCell>
                      <TableCell className="font-bold">{formatCurrency(inv.amount)}</TableCell>
                      <TableCell className="text-xs">{format(new Date(inv.due_date), "dd MMM yyyy", { locale: ar })}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status]}`}>
                          {statusLabels[inv.status] || inv.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {inv.status !== "paid" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-xs h-7 text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => markPaid.mutate(inv.id)}
                            disabled={markPaid.isPending}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            تسجيل دفع
                          </Button>
                        )}
                        {inv.status === "paid" && inv.paid_at && (
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(inv.paid_at), "dd MMM", { locale: ar })}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد فواتير</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantInvoices;
